import * as React from 'react';
import {
    Row, Col, Spin, Dropdown, Menu, Drawer, Tabs,
} from 'antd';
import {
    LeftOutlined,
    CloseOutlined,
    ExpandAltOutlined,
    SettingFilled,
    PrinterFilled,
    CloseCircleFilled,
    CheckCircleFilled,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { gridView1DetailsStyles } from './grid-view-1-details.styles';
import { Master } from 'types/master-data-types';
import { getCarrierLogo } from '../constants';
import { getDashboardStatusGroup } from '../utils';
import deliveryTruckSpeedIcon from '../../../../../assets/delivery_truck_speed.svg';
import hourglassTopIcon from '../../../../../assets/hourglass_top.svg';
import useIsMobile from 'hooks/useIsMobile';
import { StylesProps } from 'theme/jss-types';
import { HocOptions } from 'components/common/generic-hoc.types';
import GenericHoc from 'components/common/generic-hoc';
import { useLocation } from 'react-router-dom';
import { ViewData } from 'types/view.types';
import { PAGE_NAMES } from 'types/page.types';
import { isEmpty } from 'lodash';
import TimelineView from './timeline-view';
import NestedCustomColumnsModal from 'components/common/NestedCustomColumnsModal';
import {
    fetchDetailsColumns as fetchDetailsColumnsAction,
    updateDetailsColumns as updateDetailsColumnsAction,
} from 'actions/consignment-actions';
import { ReduxStore } from 'reducers/redux.types';
import ConsignmentDetailedInternational from '../../../consignment-details-international-new';
import { gridViewTabStyles } from '../grid-view-tab.styles';
import { createUseStyles } from 'react-jss';
import { GroupedColumnData } from 'components/consignments/consignment.types';
import {
    ColumnMetadata,
    generateDetailsMetadata,
    handlePrint as handlePrintUtil,
    handleOnClickPrint4X6,
    handlePrintInvoice as handlePrintInvoiceUtil,
} from '../../../consignments.helper';

interface GridView1DetailsViewProps extends StylesProps<ReturnType<typeof gridView1DetailsStyles>> {
    handleGoback: () => void;
    // referenceNumber: string;
    master: Master;
    consignment: any;
    loading: boolean;
    tracking: any[];
    trackingResult: any;
    expandPage: boolean;
    setExpandPage: (expanded: boolean) => void;
    detailsColumnsSelected?: GroupedColumnData[];
    detailsColumnsLoading?: boolean;
    consignmentSectionList?: ViewData[][];
    fetchDetailsColumns?: () => void;
    updateDetailsColumns?: (columns: GroupedColumnData[]) => Promise<void>;
}

const GridView1DetailsView: React.FC<GridView1DetailsViewProps> = (props) => {
    const {
        classes,
        handleGoback,
        // referenceNumber,
        master,
        consignment,
        loading,
        tracking,
        trackingResult,
        expandPage,
        setExpandPage,
        detailsColumnsSelected = [],
        detailsColumnsLoading = false,
        consignmentSectionList = [],
        fetchDetailsColumns,
        updateDetailsColumns,
    } = props;

    const { t, i18n } = useTranslation();
    const location = useLocation();

    // Generate metadata locally from constants
    const detailsColumnsMetadata = React.useMemo(
        () => generateDetailsMetadata(consignment?.is_international || false, t, i18n),
        [consignment?.is_international, t, i18n],
    );
    const isMobile = useIsMobile();
    const useTabStyles = createUseStyles(gridViewTabStyles);
    const tabClasses = useTabStyles();
    const [printing, setPrinting] = React.useState(false);
    const [selectedTab, setSelectedTab] = React.useState(
        (master?.Customer as any)?.default_view || 'primary_details',
    );
    const [modalVisible, setModalVisible] = React.useState(false);
    const [filteredMetadata, setFilteredMetadata] = React.useState<ColumnMetadata[]>([]);

    const piecesArray = consignment?.pieces_detail;
    const detailsTabs = [
        'primary_details',
        piecesArray?.length > 0 ? 'pieces_details' : null,
        'address_details',
    ].filter(Boolean);

    React.useEffect(() => {
        if (fetchDetailsColumns) {
            fetchDetailsColumns();
        }
    }, []);

    // Filter metadata based on view data
    React.useEffect(() => {
        const viewId = new URLSearchParams(location.search || '')?.get('view');

        if (viewId && consignmentSectionList && detailsColumnsMetadata.length > 0) {
            // Extract all column keys from metadata organized by tab_header
            const getDetailsToShowKeyList = (metadata: ColumnMetadata[]): Record<string, (string | undefined)[]> => {
                const detailsToShowKeysList: Record<string, (string | undefined)[]> = {};

                metadata.forEach((tab) => {
                    const tabHeader = tab.tab_header;
                    const keys: (string | undefined)[] = [];

                    tab.details?.forEach((detail) => {
                        detail.columns?.forEach((column) => {
                            const columnId = column.id || column.key;
                            if (columnId) {
                                keys.push(columnId);
                            }
                        });
                    });

                    if (keys.length > 0) {
                        detailsToShowKeysList[tabHeader] = keys;
                    }
                });

                return detailsToShowKeysList;
            };

            const viewData = consignmentSectionList?.flat().find((view) => view.id === viewId);

            const consignmentFieldList: Record<string, string> = viewData?.metadata?.fieldList?.reduce(
                (obj: Record<string, string>, item) => {
                    if (item?.enable) {
                        return { ...obj, [item.id]: item.name };
                    }
                    return obj;
                },
                {},
            ) || {};

            const viewDataKeys: Record<string, (string | undefined)[]> = {};

            if (!isEmpty(consignmentFieldList)) {
                const detailsToShowKeysList = getDetailsToShowKeyList(detailsColumnsMetadata);
                Object.keys(detailsToShowKeysList).forEach((key) => {
                    viewDataKeys[key] = detailsToShowKeysList[key]?.filter(
                        (item) => item && consignmentFieldList?.[item],
                    ) || [];
                });

                // Filter metadata to only include columns present in viewDataKeys
                const filteredMetadataResult = detailsColumnsMetadata.map((tab) => {
                    const tabHeader = tab.tab_header;
                    const allowedKeys = viewDataKeys[tabHeader] || [];

                    const filteredDetails = tab.details?.map((detail) => {
                        const filteredColumns = (detail.columns || [])
                            .filter((column) => {
                                const columnId = column.id || column.key;
                                return columnId && allowedKeys.includes(columnId);
                            })
                            .map((column) => {
                                const columnId = column.id || column.key;
                                // Replace pretty_name with name from view data if available
                                const viewDataName = columnId ? consignmentFieldList[columnId] : null;
                                return {
                                    ...column,
                                    pretty_name: viewDataName || column.pretty_name,
                                };
                            });

                        return {
                            ...detail,
                            columns: filteredColumns,
                        };
                    }).filter((detail) => detail.columns && detail.columns.length > 0) || [];

                    return {
                        ...tab,
                        details: filteredDetails,
                    };
                }).filter((tab) => tab.details && tab.details.length > 0);

                setFilteredMetadata(filteredMetadataResult);
            } else {
                setFilteredMetadata(detailsColumnsMetadata);
            }
        } else {
            setFilteredMetadata(detailsColumnsMetadata);
        }
    }, [consignmentSectionList, detailsColumnsMetadata, location.search]);

    const handleGearClick = () => {
        setModalVisible(true);
    };

    const handleModalSubmit = async (groupedData: GroupedColumnData[]) => {
        if (updateDetailsColumns) {
            try {
                await updateDetailsColumns(groupedData);
                return { isSuccess: true };
            } catch (error) {
                return { isSuccess: false };
            }
        }
        return { isSuccess: false };
    };

    const handleModalCancel = () => {
        setModalVisible(false);
    };

    const customerProperties = master?.properties;
    const enable_customer_config = master?.parts_to_show?.enable_customer_config || false;

    let showShipmentLabelA4 = true;
    let showShippingLabelA6 = true;
    let showshippingLabelPod = true;
    let showShippingLabel4X6 = true;
    let restrictPrintInvoice = false;
    let useThirdPartyLabel = false;

    if (customerProperties && enable_customer_config) {
        showShipmentLabelA4 = customerProperties?.a4_shipping_label;
        showShippingLabelA6 = customerProperties?.a6_shipping_label;
        showshippingLabelPod = customerProperties?.shipping_label_pod;
        showShippingLabel4X6 = customerProperties?.x46_shipping_label;
        restrictPrintInvoice = !customerProperties?.print_invoice;
        useThirdPartyLabel = customerProperties?.use_third_party_label;
    }

    const showShippingLabelButton = showShipmentLabelA4 || showShippingLabelA6
        || showshippingLabelPod || showShippingLabel4X6;

    // Print handlers
    const handlePrint = async (referenceNumbers: any, isSmall: boolean, fileName: string) => {
        setPrinting(true);
        try {
            await handlePrintUtil(referenceNumbers, isSmall, fileName, useThirdPartyLabel);
        } finally {
            setPrinting(false);
        }
    };

    const handleOnClickPrint4X6Wrapper = async (referenceNumbers: any, isInternational: boolean) => {
        setPrinting(true);
        try {
            await handleOnClickPrint4X6(referenceNumbers, isInternational);
        } finally {
            setPrinting(false);
        }
    };

    const handlePrintInvoice = async (referenceNumbers: any, fileName: string) => {
        setPrinting(true);
        try {
            await handlePrintInvoiceUtil(referenceNumbers, fileName);
        } finally {
            setPrinting(false);
        }
    };

    const renderPrintInvoice = (row: any) => {
        if (
            !row.is_international
            || !master.Customer.show_invoice_options
            || row.load_type === 'DOCUMENT'
            || restrictPrintInvoice
        ) {
            return null;
        }
        return (
            <Menu.Item
                style={{
                    padding: 12, borderBottom: '1px solid #CCCCCC', fontWeight: 'bold', fontSize: 12,
                }}
                onClick={() => handlePrintInvoice(
                    [row.reference_number],
                    `Invoice_${row.reference_number}.pdf`,
                )}
            >
                {t('print_invoice')}
            </Menu.Item>
        );
    };

    const renderPrintOptions = (row: any) => {
        return (
            <Menu>
                <Menu.Item style={{ padding: 0 }}>
                    {showShippingLabelButton && (
                        <Menu.SubMenu
                            title={(
                                <span
                                    style={{
                                        padding: 0,
                                        border: 'none',
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {t('shipping_label')}
                                </span>
                            )}
                            style={{
                                padding: '6px 0px',
                                border: 'none',
                            }}
                        >
                            {showShipmentLabelA4 && (
                                <Menu.Item
                                    style={{
                                        padding: 12, borderBottom: '1px solid #CCCCCC', fontWeight: 'bold', fontSize: 12,
                                    }}
                                    onClick={() => handlePrint(
                                        [row.reference_number],
                                        false,
                                        `Shipping_Label_${row.reference_number}.pdf`,
                                    )}
                                >
                                    {t('label_a4')}
                                </Menu.Item>
                            )}
                            {showShippingLabelA6 && (
                                <Menu.Item
                                    style={{
                                        padding: 12, borderBottom: '1px solid #CCCCCC', fontWeight: 'bold', fontSize: 12,
                                    }}
                                    onClick={() => handlePrint(
                                        [row.reference_number],
                                        true,
                                        `Shipping_Label_${row.reference_number}.pdf`,
                                    )}
                                >
                                    A6
                                </Menu.Item>
                            )}
                            {showShippingLabel4X6 && (
                                <Menu.Item
                                    style={{
                                        padding: 12, borderBottom: '1px solid #CCCCCC', fontWeight: 'bold', fontSize: 12,
                                    }}
                                    onClick={() => handleOnClickPrint4X6Wrapper(
                                        [row.reference_number], row.is_international,
                                    )}
                                >
                                    4X6
                                </Menu.Item>
                            )}
                        </Menu.SubMenu>
                    )}
                </Menu.Item>
                {renderPrintInvoice(row)}
            </Menu>
        );
    };

    const renderPrint = () => {
        const showPrintIcon = showShippingLabelButton
            || !(!consignment.is_international
                || !master.Customer.show_invoice_options
                || consignment.load_type === 'DOCUMENT'
                || restrictPrintInvoice);

        if (!showPrintIcon) {
            return null;
        }

        if (printing) {
            return <Spin />;
        }

        return !useThirdPartyLabel ? (
            <Dropdown
                disabled={printing}
                overlay={renderPrintOptions(consignment)}
                trigger={isMobile ? ['click'] : ['hover']}
            >
                <div className={classes.printButton}>
                    <PrinterFilled />
                    <span className={classes.printButtonText}>{t('label')}</span>
                </div>
            </Dropdown>
        ) : (
            <div
                className={classes.printButton}
                onClick={() => {
                    handlePrint(
                        [consignment.reference_number],
                        false,
                        `Shipping_Label_${consignment.reference_number}.pdf`,
                    );
                }}
            >
                <PrinterFilled />
                <span className={classes.printButtonText}>Label</span>
            </div>
        );
    };

    const getStatusInfo = (status: string) => {
        const statusGroup = getDashboardStatusGroup(status);

        let icon = <img src={hourglassTopIcon} alt="s" style={{ width: 14, height: 14 }} />;
        let className = classes.statusTagInProcess;

        switch (statusGroup) {
            case 'In Process':
                icon = <img src={hourglassTopIcon} alt="" style={{ width: 14, height: 14 }} />;
                className = classes.statusTagInProcess;
                break;
            case 'In Transit':
                icon = <img src={deliveryTruckSpeedIcon} alt="" style={{ width: 14, height: 14 }} />;
                className = classes.statusTagInTransit;
                break;
            case 'Delivered':
                icon = <CheckCircleFilled />;
                className = classes.statusTagDelivered;
                break;
            case 'Cancelled':
                icon = <CloseCircleFilled />;
                className = classes.statusTagCancelled;
                break;
            default:
                icon = <img src={hourglassTopIcon} alt="" style={{ width: 14, height: 14 }} />;
                className = classes.statusTagInProcess;
                break;
        }

        return { icon, className, label: statusGroup };
    };

    const renderTopSection = () => {
        if (loading) {
            return null;
        }

        const statusInfo = getStatusInfo(consignment.status);

        return (
            <div className={classes.topSection}>
                <div className={classes.topLeftSection}>
                    {!expandPage ? (
                        <CloseOutlined
                            className={classes.backButton}
                            onClick={handleGoback}
                        />
                    ) : (
                        <LeftOutlined
                            className={classes.backButton}
                            onClick={handleGoback}
                        />
                    )}
                    <div className={classes.customerReference}>
                        {consignment.customer_reference_number}
                    </div>
                    {!expandPage && (
                        <div className={classes.expandIcon}>
                            <ExpandAltOutlined onClick={() => setExpandPage(true)} />
                        </div>
                    )}
                    <div className={`${classes.statusTag} ${statusInfo.className}`}>
                        {statusInfo.icon}
                        <span>{statusInfo.label}</span>
                    </div>
                </div>
                <div className={classes.topRightSection}>
                    <div className={classes.gearButton} onClick={handleGearClick}>
                        <SettingFilled />
                    </div>
                    {renderPrint()}
                </div>
            </div>
        );
    };

    const renderContent = () => {
        if (loading || detailsColumnsLoading) {
            return (
                <div className={classes.loadingContainer}>
                    <Spin size="large" />
                </div>
            );
        }

        return (
            <div className={classes.contentSection}>
                <Row gutter={[20, 20]} className={classes.contentRow}>
                    <Col xs={24} sm={24} md={24} lg={8} xl={8} className={classes.leftColumn}>
                        <div className={classes.timelineCard}>
                            <div className={classes.timelineHeader}>
                                <div className={classes.timelineTitle}>Tracking Timeline</div>
                                {getCarrierLogo(consignment.courier_partner) && (
                                    <div className={classes.carrierLogoContainer}>
                                        <img
                                            src={getCarrierLogo(consignment.courier_partner) || ''}
                                            alt="Carrier"
                                            className={classes.carrierLogoImage}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className={classes.timelineContent}>
                                <TimelineView
                                    tracking={tracking}
                                    trackingResult={trackingResult}
                                />
                            </div>
                        </div>
                    </Col>

                    <Col xs={24} sm={24} md={24} lg={16} xl={16} className={classes.rightColumn}>
                        <Row gutter={[20, 20]}>
                            <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                                <div className={classes.infoCard}>
                                    <div className={classes.infoLabel}>{t('protocol_id')}</div>
                                    <div className={classes.infoValue}>
                                        {consignment.customer_code || 'N/A'}
                                    </div>
                                </div>
                            </Col>
                            <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                                <div className={classes.infoCard}>
                                    <div className={classes.infoLabel}>{t('awb')}</div>
                                    <div className={classes.infoValue}>
                                        {consignment.courier_partner_reference_number || 'N/A'}
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        <div className={classes.detailsCard}>
                            <Tabs
                                activeKey={selectedTab}
                                onChange={(tab) => setSelectedTab(tab)}
                                style={{ marginBottom: 16 }}
                                items={detailsTabs.map((tab) => {
                                    const tabKey = tab as string;
                                    const isPiecesTab = tabKey === 'pieces_details';
                                    // client requirement, show piece as item hence added a check based on separate_child_cns
                                    const showPieceAsItem = !master?.parts_to_show?.separate_child_cns && isPiecesTab;
                                    return {
                                        key: tabKey,
                                        label: showPieceAsItem ? t('items_details') : t(tabKey),
                                    };
                                })}
                            />

                            <ConsignmentDetailedInternational
                                consignment={consignment}
                                selectedTab={selectedTab}
                                expandPage={expandPage}
                                classes={tabClasses}
                                currencySymbol={master?.config?.currency_symbol || ''}
                                separateChildCN={!!master?.parts_to_show?.separate_child_cns}
                                completeCustomFieldsList={master?.config?.complete_custom_fields_list || []}
                                showPieceAsItem={!master?.parts_to_show?.separate_child_cns}
                            />
                        </div>
                    </Col>
                </Row>
            </div>
        );
    };

    return (
        <>
            {expandPage ? (
                <div className={classes.mainContainer}>
                    {renderTopSection()}
                    {renderContent()}
                </div>
            ) : (
                <Drawer
                    className={classes.mainDrawer}
                    visible
                    width="80%"
                    title={renderTopSection()}
                    onClose={handleGoback}
                    closable={false}
                >
                    {renderContent()}
                </Drawer>
            )}

            {modalVisible && (
                <NestedCustomColumnsModal
                    isVisible={modalVisible}
                    onModalClose={handleModalCancel}
                    onSubmit={handleModalSubmit}
                    detailsColumnsMetadata={(filteredMetadata.length > 0 ? filteredMetadata : detailsColumnsMetadata).filter(
                        (tab) => tab.tab_header !== 'Address Details',
                    )}
                    detailsColumnsSelected={detailsColumnsSelected.filter(
                        (tab) => tab.tab_header !== 'Address Details',
                    )}
                    // client requirement, show piece as item hence added a check based on separate_child_cns
                    tabHeaderDisplayMap={!master?.parts_to_show?.separate_child_cns ? {
                        'Piece Details': t('Items Details'),
                        'PIECE DETAILS': t('ITEMS DETAILS'),
                    } : undefined}
                />
            )}
        </>
    );
};

const mapStateToProps = (state: ReduxStore) => ({
    detailsColumnsLoading: state.generic.CONSIGNMENTS?.detailsColumns?.loading || false,
    detailsColumnsSelected: state.generic.CONSIGNMENTS?.detailsColumns?.selectedColumns || [],
    consignmentSectionList: state.genericPage?.[PAGE_NAMES.CONSIGNMENTS]?.metadata?.sectionList,
});

const mapDispatchToProps = (dispatch: any) => ({
    fetchDetailsColumns: () => dispatch(fetchDetailsColumnsAction()),
    updateDetailsColumns: (columns: GroupedColumnData[]) => dispatch(updateDetailsColumnsAction(columns as any)),
});

const hocConfig: HocOptions = {
    connectJss: {
        useJss: true,
        styleSheet: gridView1DetailsStyles,
    },
    connectRedux: {
        useRedux: true,
        mapStateToProps,
        mapDispatchToProps,
    },
};

const GridView1DetailsViewWithStyles = GenericHoc(hocConfig)(GridView1DetailsView);

export default GridView1DetailsViewWithStyles;
